/**
 * Synchronous wrapper for lazy-loaded Mixpanel
 * This maintains backward compatibility while using the lazy-loaded implementation
 */

import { mixpanelInstance, setupSessionTracking, cleanupSessionTracking, ensureSession, endAllSessions } from "./lazy-mixpanel";

// Define a type for the Mixpanel interface we need
interface MixpanelInterface {
  track: (event: string, properties?: any) => void;
  identify: (id: string) => void;
  people: {
    set: (properties: any) => void;
    set_once: (properties: any) => void;
    increment: (properties: any) => void;
  };
  reset: () => void;
  init: (token: string, config?: any) => void;
  [key: string]: any;
}

// Create a synchronous proxy that queues calls until mixpanel is loaded
const createSyncProxy = (): MixpanelInterface => {
  let resolvedMixpanel: any = null;
  const pendingCalls: Array<() => void> = [];

  // Load mixpanel asynchronously
  mixpanelInstance.then(mp => {
    resolvedMixpanel = mp;
    // Execute all pending calls
    pendingCalls.forEach(call => call());
    pendingCalls.length = 0;
  });

  return new Proxy({} as MixpanelInterface, {
    get(target, prop) {
      // If mixpanel is loaded, use it directly
      if (resolvedMixpanel) {
        return resolvedMixpanel[prop];
      }

      // Special handling for nested objects like 'people'
      if (prop === 'people') {
        return new Proxy({} as MixpanelInterface['people'], {
          get(target, subProp) {
            return (...args: any[]) => {
              if (resolvedMixpanel) {
                return resolvedMixpanel.people[subProp](...args);
              }
              pendingCalls.push(() => {
                if (resolvedMixpanel && resolvedMixpanel.people && resolvedMixpanel.people[subProp]) {
                  resolvedMixpanel.people[subProp](...args);
                }
              });
            };
          }
        });
      }

      // Return a function that queues the call
      return (...args: any[]) => {
        if (resolvedMixpanel) {
          const method = resolvedMixpanel[prop];
          if (typeof method === 'function') {
            return method(...args);
          }
          return method;
        }
        
        // Queue the call for when mixpanel loads
        pendingCalls.push(() => {
          const method = resolvedMixpanel[prop];
          if (typeof method === 'function') {
            method(...args);
          }
        });
      };
    }
  });
};

// Create the synchronous wrapper
const mixpanel = createSyncProxy();

// Export the sync wrapper as default
export default mixpanel;

// Re-export session functions from lazy-mixpanel
export { setupSessionTracking, cleanupSessionTracking, ensureSession, endAllSessions };